<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<crucible-dialog
  dialogTitle="Edit Event"
  [hideDefaultActions]="true"
  [guardUnsavedWork]="true"
>
  @if (data.event) {
    <div crucibleDialogContent class="event-edit-content">
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Name (required)</mat-label>
            <input type="text" matInput [formControl]="eventNameFormControl" placeholder="Name (required)"
              (blur)="saveEvent('name')" value="{{ data.event.name }}" [errorStateMatcher]="matcher" />
            @if (
              eventNameFormControl.hasError('minlength') &&
              !eventNameFormControl.hasError('required')
              ) {
              <mat-error>
                Must contain 4 or more characters
              </mat-error>
            }
            @if (eventNameFormControl.hasError('required')) {
              <mat-error>
                Name is <strong>required</strong>
              </mat-error>
            }
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Description</mat-label>
            <textarea matInput placeholder="Description" [formControl]="descriptionFormControl"
              (blur)="saveEvent('description')" value="{{ data.event.description }}"
            [errorStateMatcher]="matcher"></textarea>
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Username</mat-label>
            <input matInput placeholder="Username" [formControl]="usernameFormControl" (blur)="saveEvent('username')"
              value="{{ data.event.username }}" [errorStateMatcher]="matcher" />
            @if (usernameFormControl.hasError('required')) {
              <mat-error>
                Username is <strong>required</strong>
              </mat-error>
            }
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Status</mat-label>
            <input matInput placeholder="Status" [formControl]="statusFormControl" (blur)="saveEvent('status')"
              value="{{ data.event.status }}" [errorStateMatcher]="matcher" />
            @if (statusFormControl.hasError('required')) {
              <mat-error>
                Status is <strong>required</strong>
              </mat-error>
            }
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Internal Status</mat-label>
            <input matInput placeholder="Internal Status" [formControl]="internalStatusFormControl"
              (blur)="saveEvent('internalStatus')" value="{{ data.event.internalStatus }}"
              [errorStateMatcher]="matcher" />
            @if (internalStatusFormControl.hasError('required')) {
              <mat-error>
                Internal Status is <strong>required</strong>
              </mat-error>
            }
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>User ID</mat-label>
            <button mat-icon-button matPrefix ngxClipboard [cbContent]="data.event.userId"
              title="Copy:  {{ data.event.userId }}" style="margin-top: -15px">
              <mat-icon style="transform: scale(0.65)" svgIcon="ic_clipboard_copy"></mat-icon>
            </button>
            <input matInput placeholder="User ID" [formControl]="userIdFormControl" (blur)="saveEvent('userId')"
              value="{{ data.event.userId }}" [errorStateMatcher]="matcher" />
            @if (userIdFormControl.hasError('required')) {
              <mat-error>
                User ID is <strong>required</strong>
              </mat-error>
            }
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Event Template ID</mat-label>
            <button mat-icon-button matPrefix ngxClipboard [cbContent]="data.event.eventTemplateId"
              title="Copy:  {{ data.event.eventTemplateId }}" style="margin-top: -15px">
              <mat-icon style="transform: scale(0.65)" svgIcon="ic_clipboard_copy"></mat-icon>
            </button>
            <input matInput placeholder="Event Template ID" [formControl]="eventTemplateIdFormControl"
              (blur)="saveEvent('eventTemplateId')" value="{{ data.event.eventTemplateId }}"
              [errorStateMatcher]="matcher" />
            @if (eventTemplateIdFormControl.hasError('required')) {
              <mat-error>
                Event Template ID is <strong>required</strong>
              </mat-error>
            }
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>View ID</mat-label>
            <button mat-icon-button matPrefix ngxClipboard [cbContent]="data.event.viewId"
              title="Copy:  {{ data.event.viewId }}" style="margin-top: -15px">
              <mat-icon style="transform: scale(0.65)" svgIcon="ic_clipboard_copy"></mat-icon>
            </button>
            <input matInput placeholder="View ID" [formControl]="viewIdFormControl" (blur)="saveEvent('viewId')"
              value="{{ data.event.viewId }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Workspace ID</mat-label>
            <button mat-icon-button matPrefix ngxClipboard [cbContent]="data.event.workspaceId"
              title="Copy:  {{ data.event.workspaceId }}" style="margin-top: -15px">
              <mat-icon style="transform: scale(0.65)" svgIcon="ic_clipboard_copy"></mat-icon>
            </button>
            <input matInput placeholder="Workspace ID" [formControl]="workspaceIdFormControl"
              (blur)="saveEvent('workspaceId')" value="{{ data.event.workspaceId }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Run ID</mat-label>
            <button mat-icon-button matPrefix ngxClipboard [cbContent]="data.event.runId"
              title="Copy:  {{ data.event.runId }}" style="margin-top: -15px">
              <mat-icon style="transform: scale(0.65)" svgIcon="ic_clipboard_copy"></mat-icon>
            </button>
            <input matInput placeholder="Run ID" [formControl]="runIdFormControl" (blur)="saveEvent('runId')"
              value="{{ data.event.runId }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Scenario ID</mat-label>
            <button mat-icon-button matPrefix ngxClipboard [cbContent]="data.event.scenarioId"
              title="Copy:  {{ data.event.scenarioId }}" style="margin-top: -15px">
              <mat-icon style="transform: scale(0.65)" svgIcon="ic_clipboard_copy"></mat-icon>
            </button>
            <input matInput placeholder="Scenario ID" [formControl]="scenarioIdFormControl" (blur)="saveEvent('eventId')"
              value="{{ data.event.scenarioId }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Launch Date</mat-label>
            <input matInput placeholder="Launch Date" [formControl]="launchDateFormControl"
              (blur)="saveEvent('launchDate')" value="{{ data.event.launchDate }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>End Date</mat-label>
            <input matInput placeholder="End Date" [formControl]="endDateFormControl" (blur)="saveEvent('endDate')"
              value="{{ data.event.endDate }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Expiration Date</mat-label>
            <input matInput placeholder="Expiration Date" [formControl]="expirationDateFormControl"
              (blur)="saveEvent('expirationDate')" value="{{ data.event.expirationDate }}"
              [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
      <div class="add-margin">
        <div>
          <mat-form-field class="full-width">
            <mat-label>Status Date</mat-label>
            <input matInput placeholder="Status Date" [formControl]="statusDateFormControl"
              (blur)="saveEvent('statusDate')" value="{{ data.event.statusDate }}" [errorStateMatcher]="matcher" />
          </mat-form-field>
        </div>
      </div>
    </div>
  }
  <div crucibleDialogActions class="bottom-button">
    <div class="delete-button">
      <button matButton="outlined" (click)="handleEditComplete(false)">Cancel</button>
    </div>
    <div class="delete-button">
      <button matButton="filled" (click)="handleEditComplete(true)" [disabled]="!data.canEdit">Save</button>
    </div>
    <span class="spacer"></span>
    <div class="delete-button">
      @if (data.event.status !== 'Ended') {
        <button matButton="outlined" (click)="endEvent()" [disabled]="!data.canManage">
          End Event Now
        </button>
      }
    </div>
    <div class="delete-button">
      <button matButton="outlined" (click)="deleteEvent()" [disabled]="!data.canManage">Delete Event</button>
    </div>
  </div>
</crucible-dialog>
